import { useId, useMemo, useRef, useState } from "react";
import { mergeProps, useButton, useFocusRing, useModalOverlay } from "react-aria";
import { useOverlayTriggerState } from "react-stately";
import { tv } from "tailwind-variants";

import { useDomRef } from "../../hooks/useDomRef";
import { cn } from "../../utils/cn";
import { dataFocusVisible } from "../../utils/tailwindClasses";

import type { CSSProperties, HTMLAttributes, ReactNode, RefObject } from "react";
import type { AriaModalOverlayProps } from "react-aria";
import type { OverlayTriggerProps } from "react-stately";
import type { VariantProps } from "tailwind-variants";
import type { DOMAttributes, ReactRef, SlotsToClasses } from "../../utils/types";

const drawerVariants = tv({
  slots: {
    wrapper: "fixed inset-0 z-50 flex",
    base: [
      "relative",
      "box-border",
      "flex",
      "flex-col",
      "bg-white",
      "dark:bg-gray-850",
      "dark:text-white",
      "shadow-lg",
      "outline-none",
      "overflow-hidden",
    ],
    backdrop: "fixed inset-0 z-40 bg-gray-900/30 backdrop-blur-sm",
    closeButton: [
      "absolute",
      "appearance-none",
      "border-none",
      "bg-transparent",
      "outline-none",
      "select-none",
      "cursor-pointer",
      "top-3.5",
      "right-3.5",
      "w-8",
      "h-8",
      "text-base",
      "text-gray-500",
      "rounded-full",
      "hover:bg-gray-50",
      "dark:hover:bg-gray-800",
      "active:bg-gray-200",
      "dark:active:bg-gray-700",
      "transition-colors",
      "tap-highlight-transparent",
      ...dataFocusVisible,
    ],
    header:
      "flex flex-initial flex-col gap-1.5 border-b border-gray-200 px-6 py-4 dark:border-gray-700",
    title: "text-lg font-semibold",
    body: "flex flex-1 flex-col overflow-y-auto",
    footer:
      "flex flex-row justify-end gap-2 border-t border-gray-200 px-6 py-4 dark:border-gray-700",
  },
  variants: {
    placement: {
      left: {
        wrapper: "justify-start",
        base: "h-full",
      },
      right: {
        wrapper: "justify-end",
        base: "h-full",
      },
      top: {
        wrapper: "flex-col items-start",
        base: "w-full",
      },
      bottom: {
        wrapper: "flex-col items-end",
        base: "w-full",
      },
    },
    size: {
      sm: {},
      default: {},
      lg: {},
      xl: {},
    },
  },
  compoundVariants: [
    // Left/Right placements (horizontal) - use width
    { placement: "left", size: "sm", class: { base: "w-64" } },
    { placement: "left", size: "default", class: { base: "w-80" } },
    { placement: "left", size: "lg", class: { base: "w-96" } },
    { placement: "left", size: "xl", class: { base: "w-[480px]" } },
    { placement: "right", size: "sm", class: { base: "w-64" } },
    { placement: "right", size: "default", class: { base: "w-80" } },
    { placement: "right", size: "lg", class: { base: "w-96" } },
    { placement: "right", size: "xl", class: { base: "w-[480px]" } },
    // Top/Bottom placements (vertical) - use height
    { placement: "top", size: "sm", class: { base: "h-32" } },
    { placement: "top", size: "default", class: { base: "h-64" } },
    { placement: "top", size: "lg", class: { base: "h-96" } },
    { placement: "top", size: "xl", class: { base: "h-[480px]" } },
    { placement: "bottom", size: "sm", class: { base: "h-32" } },
    { placement: "bottom", size: "default", class: { base: "h-64" } },
    { placement: "bottom", size: "lg", class: { base: "h-96" } },
    { placement: "bottom", size: "xl", class: { base: "h-[480px]" } },
  ],
  defaultVariants: {
    placement: "right",
    size: "default",
  },
});

export interface UseDrawerProps
  extends Omit<HTMLAttributes<HTMLElement>, "title">,
    OverlayTriggerProps,
    AriaModalOverlayProps,
    VariantProps<typeof drawerVariants> {
  /** Whether the drawer is open (controlled). Alias for `isOpen` for antd compatibility. */
  visible?: boolean;
  /** Whether to hide the close button */
  hideCloseButton?: boolean;
  /** Alias for hideCloseButton - when false, close button is hidden. For antd compatibility. */
  closable?: boolean;
  /** Container element for the portal */
  portalContainer?: Element;
  /** Callback when drawer closes */
  onClose?: () => void;
  /** Title displayed in the header */
  title?: React.ReactNode;
  /** Whether to show the backdrop/mask (default: true) */
  mask?: boolean;
  /** Custom width for the drawer (overrides size variant for left/right placement) */
  width?: number | string;
  /** Custom height for the drawer (overrides size variant for top/bottom placement) */
  height?: number | string;
  /** Custom styles for the body */
  bodyStyle?: CSSProperties;
  /** Custom class names for slots */
  classNames?: SlotsToClasses<keyof ReturnType<typeof drawerVariants>>;
}

export type DrawerPlacement = "left" | "right" | "top" | "bottom";

export interface UseDrawerReturn {
  slots: ReturnType<typeof drawerVariants>;
  domRef: RefObject<HTMLElement | null>;
  headerId: string;
  bodyId: string;
  classNames: SlotsToClasses<keyof ReturnType<typeof drawerVariants>> | undefined;
  isDismissable: boolean;
  portalContainer: Element | undefined;
  isOpen: boolean;
  onClose: () => void;
  setBodyMounted: (mounted: boolean) => void;
  setHeaderMounted: (mounted: boolean) => void;
  getDialogProps: (props?: DOMAttributes) => {
    className: string;
    style: CSSProperties;
    id: string;
    "data-open": boolean;
    "data-dismissable": boolean;
    "data-placement": DrawerPlacement;
    "aria-modal": boolean;
    "aria-labelledby": string | undefined;
    "aria-describedby": string | undefined;
    [key: string]: unknown;
  };
  backdropProps: Record<string, unknown> & {
    className: string;
  };
  closeButtonProps: Record<string, unknown> & {
    className: string;
  };
  hideCloseButton: boolean;
  title: ReactNode;
  mask: boolean;
  bodyStyle: CSSProperties | undefined;
  placement: DrawerPlacement;
}

export const useDrawer = (props: UseDrawerProps, ref: ReactRef<HTMLElement>): UseDrawerReturn => {
  const {
    className,
    classNames,
    isOpen,
    visible,
    defaultOpen,
    onOpenChange,
    isDismissable = true,
    portalContainer,
    isKeyboardDismissDisabled,
    onClose,
    hideCloseButton,
    closable = true,
    title,
    mask = true,
    width,
    height,
    bodyStyle,
    size,
    placement = "right",
    shouldCloseOnInteractOutside,
    ...otherProps
  } = props;

  const domRef = useDomRef(ref);
  const closeButtonRef = useRef<HTMLElement>(null);

  const [headerMounted, setHeaderMounted] = useState(false);
  const [bodyMounted, setBodyMounted] = useState(false);

  const dialogId = useId();
  const headerId = useId();
  const bodyId = useId();

  // Support both `isOpen` and `visible` for antd compatibility
  const controlledIsOpen = visible ?? isOpen;

  const state = useOverlayTriggerState({
    isOpen: controlledIsOpen,
    defaultOpen,
    onOpenChange: isOpen => {
      onOpenChange?.(isOpen);
      if (!isOpen) {
        onClose?.();
      }
    },
  });

  const { modalProps, underlayProps } = useModalOverlay(
    {
      isDismissable,
      isKeyboardDismissDisabled,
      shouldCloseOnInteractOutside,
    },
    state,
    domRef
  );

  const { buttonProps: ariaCloseButtonProps } = useButton({ onPress: state.close }, closeButtonRef);
  const { isFocusVisible: isCloseButtonFocusVisible, focusProps: closeButtonFocusProps } =
    useFocusRing();

  const slots = useMemo(() => drawerVariants({ size, placement }), [size, placement]);

  // Calculate custom dimensions
  const customDimensions = useMemo(() => {
    const style: CSSProperties = {};
    if (placement === "left" || placement === "right") {
      if (width) {
        style.width = typeof width === "number" ? `${width}px` : width;
      }
    } else {
      if (height) {
        style.height = typeof height === "number" ? `${height}px` : height;
      }
    }
    return style;
  }, [placement, width, height]);

  // Determine if close button should be shown
  const shouldHideCloseButton = hideCloseButton ?? !closable;

  const getDialogProps = (props: DOMAttributes = {}) => {
    const merged = mergeProps(modalProps, otherProps, props);
    return {
      ref: domRef,
      ...merged,
      className: slots.base({ className: cn(classNames?.base, className, props.className) }),
      style: { ...customDimensions, ...(merged.style ?? {}), ...(props.style ?? {}) },
      id: dialogId,
      "data-open": state.isOpen,
      "data-dismissable": isDismissable,
      "data-placement": placement,
      "aria-modal": true,
      "aria-labelledby": headerMounted ? headerId : undefined,
      "aria-describedby": bodyMounted ? bodyId : undefined,
    };
  };

  const backdropProps = useMemo(
    () => ({
      className: slots.backdrop({ className: classNames?.backdrop }),
      onClick: () => state.close,
      ...underlayProps,
    }),
    [slots, classNames, underlayProps, state.close]
  );

  const closeButtonProps = useMemo(
    () => ({
      role: "button",
      tabIndex: 0,
      "aria-label": "Close",
      "data-focus-visible": isCloseButtonFocusVisible,
      className: slots.closeButton({ className: classNames?.closeButton }),
      ...mergeProps(ariaCloseButtonProps, closeButtonFocusProps),
    }),
    [slots, classNames, ariaCloseButtonProps, closeButtonFocusProps, isCloseButtonFocusVisible]
  );

  return {
    slots,
    domRef,
    headerId,
    bodyId,
    classNames,
    isDismissable,
    portalContainer,
    isOpen: state.isOpen,
    onClose: state.close,
    setBodyMounted,
    setHeaderMounted,
    getDialogProps,
    backdropProps,
    closeButtonProps,
    hideCloseButton: shouldHideCloseButton,
    title,
    mask,
    bodyStyle,
    placement,
  };
};
